import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Check, ChevronRight, Sparkles, Heart } from "lucide-react";
import { toast } from "sonner";

const STYLE_OPTIONS = [
  { id: "ART_DECO", labelKey: "quiz.style_art_deco", descKey: "quiz.style_art_deco_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-cornisa01.jpg" },
  { id: "CLASSIC", labelKey: "quiz.style_classic", descKey: "quiz.style_classic_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-rozete-plafon01.jpg" },
  { id: "MODERN", labelKey: "quiz.style_modern", descKey: "quiz.style_modern_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-scafe-decorative01.jpg" },
  { id: "NEOCLASSIC", labelKey: "quiz.style_neoclassic", descKey: "quiz.style_neoclassic_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/exterior-pilastrii.01jpg.jpg" },
];

const ELEMENT_OPTIONS = [
  { id: "cornisa", labelKey: "quiz.element_cornisa", descKey: "quiz.element_cornisa_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-masca-galerie01.jpg.webp" },
  { id: "brau", labelKey: "quiz.element_brau", descKey: "quiz.element_brau_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-brauri-decorative01.jpg.webp" },
  { id: "panou_3d", labelKey: "quiz.element_panou3d", descKey: "quiz.element_panou3d_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/interior-panouri-decorative01.jpg" },
  { id: "ancadrament", labelKey: "quiz.element_ancadrament", descKey: "quiz.element_ancadrament_desc", img: "https://www.fabricadeprofile.ro/wp-content/uploads/2022/05/exterior-ancadramente01.jpg" },
];

const SPACE_OPTIONS = [
  { id: "living", labelKey: "quiz.space_living" },
  { id: "dormitor", labelKey: "quiz.space_dormitor" },
  { id: "hol", labelKey: "quiz.space_hol" },
  { id: "fatada", labelKey: "quiz.space_fatada" },
];

const BUDGET_OPTIONS = [
  { id: "<1000", labelKey: "quiz.budget_under_1k" },
  { id: "1000-3000", labelKey: "quiz.budget_1k_3k" },
  { id: "3000-7000", labelKey: "quiz.budget_3k_7k" },
  { id: ">7000", labelKey: "quiz.budget_over_7k" },
];

export default function StyleQuiz({ simulationId, currentStyle, onComplete }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({
    favourite_style: currentStyle || null,
    favourite_element: null,
    space: null,
    budget_range: null,
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/quiz", { simulation_id: simulationId, ...answers });
      setDone(true);
      toast.success(t("quiz.toast_thanks"));
      if (onComplete) onComplete(answers);
    } catch (e) {
      toast.error(t("quiz.toast_error"));
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => {
    if (step < 3) setStep(step + 1);
    else submit();
  };

  if (done) {
    return (
      <div
        className="border border-orange-500/30 bg-orange-500/5 rounded-lg p-8 text-center"
        data-testid="quiz-done"
      >
        <div className="mx-auto w-14 h-14 rounded-full bg-orange-500 flex items-center justify-center mb-4">
          <Heart size={26} strokeWidth={1.5} className="text-white" fill="white" />
        </div>
        <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2">{t("quiz.done_eyebrow")}</div>
        <h3 className="font-outfit font-bold text-white text-2xl tracking-tighter">
          {t("quiz.done_title")}
        </h3>
        <p className="text-zinc-400 max-w-md mx-auto mt-3 text-sm">
          {t("quiz.done_body")}
        </p>
      </div>
    );
  }

  const QUESTIONS = [
    { key: "favourite_style", titleKey: "quiz.q_style_title", options: STYLE_OPTIONS, visual: true },
    { key: "favourite_element", titleKey: "quiz.q_element_title", options: ELEMENT_OPTIONS, visual: true },
    { key: "space", titleKey: "quiz.q_space_title", options: SPACE_OPTIONS, visual: false },
    { key: "budget_range", titleKey: "quiz.q_budget_title", options: BUDGET_OPTIONS, visual: false },
  ];

  const q = QUESTIONS[step];
  const value = answers[q.key];

  return (
    <div
      className="border border-orange-500/20 bg-zinc-900/30 rounded-lg p-6 md:p-8"
      data-testid="style-quiz"
    >
      <div className="flex items-center justify-between mb-5">
        <div className="text-xs uppercase tracking-[0.3em] text-orange-500 flex items-center gap-2">
          <Sparkles size={12} className="animate-pulse" /> {t("quiz.header", { current: step + 1, total: 4 })}
        </div>
        <div className="flex items-center gap-1">
          {QUESTIONS.map((qq, i) => (
            <div
              key={qq.key}
              className={`h-1 rounded-full transition-all ${
                i < step
                  ? "w-8 bg-orange-500"
                  : i === step
                  ? "w-12 bg-orange-500"
                  : "w-4 bg-zinc-700"
              }`}
            />
          ))}
        </div>
      </div>

      <h3 className="font-outfit font-bold text-white text-xl md:text-2xl tracking-tight mb-6">
        {t(q.titleKey)}
      </h3>

      {q.visual ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {q.options.map((opt) => {
            const selected = value === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setAnswers({ ...answers, [q.key]: opt.id })}
                className={`relative aspect-[3/4] rounded-lg overflow-hidden border-2 transition-all ${
                  selected
                    ? "border-orange-500 ring-2 ring-orange-500/30"
                    : "border-white/10 hover:border-white/30"
                }`}
                data-testid={`quiz-opt-${opt.id}`}
              >
                <img
                  src={opt.img}
                  alt={t(opt.labelKey)}
                  className="absolute inset-0 w-full h-full object-cover"
                  onError={(e) => (e.currentTarget.style.display = "none")}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
                {selected && (
                  <div className="absolute top-2 right-2 w-7 h-7 rounded-full bg-orange-500 flex items-center justify-center">
                    <Check size={14} strokeWidth={2.5} className="text-white" />
                  </div>
                )}
                <div className="absolute inset-x-0 bottom-0 p-3 text-left">
                  <div className="font-outfit font-bold text-white text-sm">{t(opt.labelKey)}</div>
                  <div className="text-[11px] text-zinc-300/80 truncate">{t(opt.descKey)}</div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {q.options.map((opt) => {
            const selected = value === opt.id;
            return (
              <button
                key={opt.id}
                onClick={() => setAnswers({ ...answers, [q.key]: opt.id })}
                className={`p-4 rounded-lg border-2 text-center transition-all ${
                  selected
                    ? "border-orange-500 bg-orange-500/5 text-white"
                    : "border-white/10 bg-zinc-950/50 text-zinc-300 hover:border-white/30"
                }`}
                data-testid={`quiz-opt-${opt.id}`}
              >
                <div className="font-outfit font-bold text-sm">{t(opt.labelKey)}</div>
                {selected && (
                  <Check size={14} strokeWidth={2.5} className="mx-auto mt-2 text-orange-500" />
                )}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          onClick={() => (step > 0 ? setStep(step - 1) : null)}
          disabled={step === 0}
          className="text-sm text-zinc-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition"
          data-testid="quiz-back"
        >
          {t("quiz.back")}
        </button>
        <div className="flex items-center gap-3">
          <button
            onClick={() => (step < 3 ? setStep(step + 1) : submit())}
            className="text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 transition"
            data-testid="quiz-skip"
          >
            {t("quiz.skip")}
          </button>
          <button
            onClick={next}
            disabled={submitting || !value}
            className="px-5 py-2.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition btn-orange-glow"
            data-testid="quiz-next"
          >
            {step < 3 ? t("quiz.next") : submitting ? t("quiz.submit_loading") : t("quiz.submit")}
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
